/**
 * Repeating Clouds
 * Handles the repeating cloud pattern dividers between sections.
 * - Detects sibling section background colours
 * - Sets the cloud SVG fill to match the current section's background
 * - Sets the cloud div background to match the sibling section's background
 * - Animates the clouds using requestAnimationFrame for smooth scrolling
 */

// Animation speed (pixels per second)
const ANIMATION_SPEED = 30;

// Store active cloud instances for animation
const cloudInstances = [];

/**
 * Get the computed background color of an element as a hex value
 */
function getBackgroundColor(element) {
    if (!element) return '#FDFAFA';
    const computedStyle = window.getComputedStyle(element);
    const bgColor = computedStyle.backgroundColor;
    if (bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
        return element.parentElement ? getBackgroundColor(element.parentElement) : '#FDFAFA';
    }
    const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
        const r = parseInt(match[1]).toString(16).padStart(2, '0');
        const g = parseInt(match[2]).toString(16).padStart(2, '0');
        const b = parseInt(match[3]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }
    return '#FDFAFA';
}

/**
 * Find the previous visible sibling section element
 */
function getPreviousSiblingSection(element) {
    let sibling = element.previousElementSibling;
    while (sibling) {
        if (sibling.tagName === 'SECTION' || sibling.tagName === 'HEADER' ||
            sibling.tagName === 'ARTICLE' || sibling.classList.contains('content-panel') ||
            sibling.classList.contains('content-panel-wrapper')) {
            return sibling;
        }
        sibling = sibling.previousElementSibling;
    }
    return null;
}

/**
 * Find the next visible sibling section element
 */
function getNextSiblingSection(element) {
    let sibling = element.nextElementSibling;
    while (sibling) {
        if (sibling.tagName === 'SECTION' || sibling.tagName === 'FOOTER' ||
            sibling.tagName === 'ARTICLE' || sibling.classList.contains('content-panel') ||
            sibling.classList.contains('content-panel-wrapper')) {
            return sibling;
        }
        sibling = sibling.nextElementSibling;
    }
    return null;
}

/**
 * Setup the track by cloning SVG patterns to fill the width
 * Returns the actual rendered pattern width
 */
function setupTrack(track, container, fillColor) {
    // Get the template SVG (first one in the track)
    const templateSvg = track.querySelector('[data-cloud-pattern]');
    if (!templateSvg) return 0;

    // Set fill color on the template
    const paths = templateSvg.querySelectorAll('path');
    paths.forEach(path => path.setAttribute('fill', fillColor));

    // Measure the actual rendered width of the pattern
    const patternWidth = templateSvg.getBoundingClientRect().width;
    if (patternWidth === 0) return 0;

    const containerWidth = container.offsetWidth;
    // Need enough patterns to cover container + 2 extra for seamless looping
    const requiredWidth = containerWidth + (patternWidth * 2);
    const patternCount = Math.ceil(requiredWidth / patternWidth) + 1;

    // Remove any previously cloned SVGs (keep only the template)
    const existingSvgs = track.querySelectorAll('[data-cloud-pattern]');
    existingSvgs.forEach((svg, index) => {
        if (index > 0) svg.remove();
    });

    // Clone the SVG to fill the track
    for (let i = 1; i < patternCount; i++) {
        const clone = templateSvg.cloneNode(true);
        track.appendChild(clone);
    }

    return patternWidth;
}

/**
 * Cloud instance class to manage animation state
 */
class CloudInstance {
    constructor(container, track, direction, fillColor) {
        this.container = container;
        this.track = track;
        this.direction = direction; // 'left' or 'right'
        this.fillColor = fillColor;
        this.patternWidth = 0;
        this.position = 0;
        this.lastTime = null;
    }

    setup() {
        this.patternWidth = setupTrack(this.track, this.container, this.fillColor);
        // Set initial position based on direction
        // For 'right' movement: start at -patternWidth so we have room to move right
        // For 'left' movement: start at 0 so we have room to move left
        this.position = this.direction === 'right' ? -this.patternWidth : 0;
        this.updatePosition();
    }

    updatePosition() {
        this.track.style.transform = `translateX(${this.position}px)`;
    }

    animate(currentTime) {
        if (!this.lastTime) {
            this.lastTime = currentTime;
        }

        if (this.patternWidth === 0) return;

        const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
        this.lastTime = currentTime;

        // Move based on direction
        if (this.direction === 'left') {
            this.position -= ANIMATION_SPEED * deltaTime;
            // Reset when we've moved one pattern width
            if (this.position <= -this.patternWidth) {
                this.position += this.patternWidth;
            }
        } else {
            this.position += ANIMATION_SPEED * deltaTime;
            // Reset when we've moved back to 0
            if (this.position >= 0) {
                this.position -= this.patternWidth;
            }
        }

        this.updatePosition();
    }
}

/**
 * Initialize the repeating clouds for a section
 */
function initRepeatingClouds(section) {
    const currentBgColor = getBackgroundColor(section);

    // Handle top clouds
    const topClouds = section.querySelector('[data-repeating-clouds="top"]');
    if (topClouds) {
        const track = topClouds.querySelector('[data-cloud-track]');
        if (track) {
            const previousSection = getPreviousSiblingSection(section);
            const previousBgColor = previousSection ? getBackgroundColor(previousSection) : currentBgColor;

            // Set container background to match previous section
            topClouds.style.backgroundColor = previousBgColor;

            // Create cloud instance (clouds fill with current section color)
            // For clouds to APPEAR to move right, track moves LEFT
            const instance = new CloudInstance(topClouds, track, 'left', currentBgColor);
            instance.setup();
            cloudInstances.push(instance);
        }
    }

    // Handle bottom clouds
    const bottomClouds = section.querySelector('[data-repeating-clouds="bottom"]');
    if (bottomClouds) {
        const track = bottomClouds.querySelector('[data-cloud-track]');
        if (track) {
            const nextSection = getNextSiblingSection(section);
            const nextBgColor = nextSection ? getBackgroundColor(nextSection) : currentBgColor;

            // Set container background to match next section
            bottomClouds.style.backgroundColor = nextBgColor;

            // Create cloud instance (clouds fill with current section color)
            // For clouds to APPEAR to move left, track moves RIGHT
            const instance = new CloudInstance(bottomClouds, track, 'right', currentBgColor);
            instance.setup();
            cloudInstances.push(instance);
        }
    }
}

/**
 * Animation loop using requestAnimationFrame
 */
function animationLoop(currentTime) {
    cloudInstances.forEach(instance => instance.animate(currentTime));
    requestAnimationFrame(animationLoop);
}

/**
 * Handle window resize - recalculate track widths
 */
function handleResize() {
    cloudInstances.forEach(instance => instance.setup());
}

/**
 * Initialize all repeating clouds on the page
 */
function initAllRepeatingClouds() {
    const sectionsWithClouds = document.querySelectorAll('[data-repeating-clouds]');
    const processedSections = new Set();

    sectionsWithClouds.forEach(cloudElement => {
        const section = cloudElement.closest('section, article, header, footer, [class*="content-panel"]');
        if (section && !processedSections.has(section)) {
            processedSections.add(section);
            initRepeatingClouds(section);
        }
    });

    // Start animation loop if we have instances
    if (cloudInstances.length > 0) {
        requestAnimationFrame(animationLoop);
    }
}

// Initialize on page load
window.addEventListener('load', initAllRepeatingClouds);

// Handle resize with debounce
let resizeTimeout;
window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleResize, 100);
});

